import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  QUESTION_AGE_GROUP_ID,
  QUESTION_CONTACT_INFO_ID,
  QUESTION_GENDER_IDENTIFY_ID
} from '../../constants/new-form-constants';

interface Settings {
  language: string;
  progress_bar: string;
  meta: {
    allow_indexing: boolean;
  };
  hide_navigation: boolean;
  is_public: boolean;
  is_trial: boolean;
  show_progress_bar: boolean;
  show_typeform_branding: boolean;
  are_uploads_public: boolean;
  show_time_to_complete: boolean;
  show_number_of_submissions: boolean;
  show_cookie_consent: boolean;
  show_question_number: boolean;
  show_key_hint_on_choices: boolean;
  autosave_progress: boolean;
  free_form_navigation: boolean;
  use_lead_qualification: boolean;
  pro_subdomain_enabled: boolean;
  auto_translate: boolean;
}

export interface Choice {
  ref: string;
  id: string;
  label: string;
}

export interface ContactField {
  id: string;
  ref: string;
  title: string;
  type: string;
  validations: Validations;
  properties: Properties;
  subfield_key: string;
}

export interface Properties {
  randomize?: boolean;
  allow_multiple_selection?: boolean;
  allow_other_choice?: boolean;
  vertical_alignment?: boolean;
  description?: string;
  choices?: Choice[];
  shape?: string;
  steps?: number;
  contactFields?: ContactField[];
}

interface Validations {
  required?: boolean;
  min_selection?: number;
  max_selection?: number;
}

export interface Question {
  id: string;
  ref: string;
  title: string;
  type: 'multiple_choice' | 'rating' | 'long_text' | 'contact_info' | string;
  validations: Validations;
  properties: Properties;
}

export interface ContactInfo {
  first_name: string;
  last_name: string;
  location: string;
  email: string;
}

export interface Answer {
  type: 'multiple_choice' | 'rating' | 'long_text' | 'contact_info' | string;
  question: {
    type: string;
    id: string;
    properties: Properties;
  };
  text?: string;
  choices?: Choice[];
  contact_info?: ContactInfo;
  number?: number;
}

interface Form {
  id: string;
  title: string;
  settings: Settings;
  questions: Question[];
}

export interface ClientRequestNewHealerState {
  form: Form;
  version: number;
  answers: Record<string, Answer>;
  submitStatus: 'loading' | 'fail' | 'success' | null;
  recommendationRequestId: string | null;
  clientId: string | null;
}

export const initialClientRequestNewHealerState = (): ClientRequestNewHealerState => {
  return {
    form: {
      id: 'H2yM9Enh',
      title: 'Start your Journey (V2.1)',
      settings: {
        language: 'en',
        progress_bar: 'proportion',
        meta: {
          allow_indexing: false,
        },
        hide_navigation: false,
        is_public: false,
        is_trial: false,
        show_progress_bar: true,
        show_typeform_branding: false,
        are_uploads_public: false,
        show_time_to_complete: true,
        show_number_of_submissions: false,
        show_cookie_consent: false,
        show_question_number: true,
        show_key_hint_on_choices: true,
        autosave_progress: true,
        free_form_navigation: false,
        use_lead_qualification: false,
        pro_subdomain_enabled: true,
        auto_translate: true,
      },
      questions: [
        {
          id: 'PdHyZXW62w1F',
          ref: '043413fe-82f8-4634-842d-397cbcbc618a',
          title: 'Have you ever tried energy healing?',
          type: 'multiple_choice',
          validations: {
            required: true,
          },
          properties: {
            randomize: false,
            allow_multiple_selection: false,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: '7c2c6d4a-6dec-44ba-83ce-8c0ebb0dca1a',
                id: 'TmYOquIqbtcp',
                label: 'Yes',
              },
              {
                ref: 'a396e4ef-f127-47d6-a62f-fe5fe5616f9f',
                id: 'Bz10zWrgLxES',
                label: 'No',
              },
            ],
          },
        },
        {
          id: '2Xo3HcZnD8bt',
          ref: 'fba643d1-54f9-4972-af82-362752fb2e8b',
          title: 'How much do you trust energy healing? \n\n(1 = not at all; 5 = very much)',
          type: 'rating',
          validations: {
            required: true,
          },
          properties: {
            shape: 'heart',
            steps: 5,
          },
        },
        {
          id: 'ZiYpC44LuZCt',
          ref: '48ecda95-8d77-4b5a-bb48-f47db9f90269',
          title: 'Energy healing can improve many aspects of your life.\n \nWhich **physical benefits** are you most interested in?',
          type: 'multiple_choice',
          validations: {
            required: true,
            min_selection: 1,
            max_selection: 3,
          },
          properties: {
            description: '_Select up to 3._',
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: 'def6c83d-1e19-4272-ae27-b9d9bc566f17',
                id: 'h34ga6ZYrguL',
                label: 'Relief from chronic pain',
              },
              {
                ref: 'c7544d6a-a9de-43fe-bc8e-a8efcc9903c9',
                id: '27BHMLLjWSSd',
                label: 'Recovery from injury / surgery',
              },
              {
                ref: '65ced683-c2b7-40dd-825b-470bda34d49c',
                id: 'GHzNY82i3pXf',
                label: 'Heal scars, warts, skin ailments',
              },
              {
                ref: 'b0cda3d3-fe90-466f-a9b7-a22eb946e51c',
                id: 'Qm3HvvvU6Nsr',
                label: 'Balanced hormones / fertility',
              },
              {
                ref: 'bbfc8b4c-db2a-4469-b444-9a358c704d10',
                id: 'JmHPOOqHtFEx',
                label: 'Reduced inflammation',
              },
              {
                ref: '4c2514db-af83-4020-9187-9117e15dca9b',
                id: '1XUN7qIiSq7u',
                label: 'None of these',
              },
            ],
          },
        },
        {
          id: 'CaN16hgTblHW',
          ref: '67fe3249-3747-47e2-9428-d9b5f14cc320',
          title: 'And how about **mental benefits**?',
          type: 'multiple_choice',
          validations: {
            required: true,
            min_selection: 1,
            max_selection: 3,
          },
          properties: {
            description: '_Select up to 3._',
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: 'ba3bf891-37f8-4c9d-8fe1-b7d9d71b8a2a',
                id: 'vJjim8yA9PBD',
                label: 'Anxiety relief',
              },
              {
                ref: '3e9e4377-1943-47d0-97ab-49291b5c9d08',
                id: 'Vr3iOO6u1Yj2',
                label: 'Improved focus & clarity',
              },
              {
                ref: '2b4e30b0-1cbe-4774-97a0-dbf8058bd2af',
                id: 'xBR2VFc2gq7b',
                label: 'Relaxed state of mind',
              },
              {
                ref: 'bb78fabe-eebf-4d00-9e5f-b6d7d054f113',
                id: 'Vuyuwn45Sgd8',
                label: 'Appreciation for life',
              },
              {
                ref: '5629fb64-20e1-4a05-aa94-3273a96d6db0',
                id: 'eE3LDCXykyO2',
                label: 'Manifesting my goals',
              },
              {
                ref: '9054fe9a-f181-4cda-ab9a-02c072a0bc63',
                id: 'ISQmsGKXWFub',
                label: 'More open mind',
              },
              {
                ref: '9385c664-84a4-47ef-ae8b-205b1ac0a02a',
                id: 'NMiPRCzB2EIF',
                label: 'None of these',
              },
            ],
          },
        },
        {
          id: 'bqmOuYotyBU8',
          ref: 'a02fc273-d936-4b71-8a92-cd88402d79e2',
          title: 'Are you interested in any of the following **energetic benefits**?',
          type: 'multiple_choice',
          validations: {
            required: true,
            min_selection: 1,
            max_selection: 3,
          },
          properties: {
            description: '_Select no more than 3._',
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: '3a54a853-a104-45be-9b17-112ad23da9a5',
                id: 'dRJfqSVmPvfd',
                label: 'Boost in energy',
              },
              {
                ref: '9ce2fa2a-c4d7-469b-ba02-6ac24c00c58e',
                id: 'Wc6c2JgzINFN',
                label: 'Accomplishing goals',
              },
              {
                ref: '3b237e51-bcf4-4a39-9709-23767d2307e3',
                id: 'dA85Cq38a7vY',
                label: 'Opening of life path',
              },
              {
                ref: '9f010a5d-42e0-48dd-ab02-51cc8cc3499c',
                id: 'h36Y6zmhd8OB',
                label: 'Aligning with life purpose',
              },
              {
                ref: '34d929be-3ad3-48a4-9064-61667376a92a',
                id: 'naEkI0dP6ylV',
                label: 'Increased serendipity',
              },
              {
                ref: 'b8bac213-3a77-4c79-9a70-d7e79ba1b21a',
                id: 'T83Z8uYRtQJH',
                label: 'Restore balance and flow',
              },
              {
                ref: 'a429d28e-93af-4e02-88aa-5ccb3a3509ae',
                id: 'jKxGOghKbU7E',
                label: 'None of these',
              },
            ],
          },
        },
        {
          id: 'Bu4RBFVvYKxv',
          ref: '2f892479-aeef-4fb4-95ec-937a65a4eebc',
          title: 'Or any **emotional benefits** of energy healing?',
          type: 'multiple_choice',
          validations: {
            required: true,
            min_selection: 1,
            max_selection: 3,
          },
          properties: {
            description: '_Select up to 3._',
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: 'f8c0173d-35ae-42db-83aa-c54821ff05e5',
                id: 'V134y4niLnWU',
                label: 'Stronger boundaries',
              },
              {
                ref: '89400e80-daff-4cd3-b512-ac90da1365ab',
                id: 'J0nveyoFrtJo',
                label: 'Overcoming relationship issues',
              },
              {
                ref: '68befa26-4192-45d0-959b-4bf0b16a390a',
                id: 'I0QsCf5ERtV0',
                label: 'Understanding of self',
              },
              {
                ref: 'd9938140-739b-46ba-8295-fdefcc158b71',
                id: 'mLAAX8KTJrlL',
                label: 'Developed intuition',
              },
              {
                ref: '359fc6e2-eebd-4d61-9f1e-af264d146871',
                id: 'hRxI7TKKUb5O',
                label: 'Affirming life choices',
              },
              {
                ref: '34280313-6698-45b2-aed9-e45777fa2e3d',
                id: 'OeltUdkK45uw',
                label: 'Increased joy',
              },
              {
                ref: '4e18e72c-78b5-42bd-92b5-c69320031b3b',
                id: 'XNrhSS63M96P',
                label: 'None of these',
              },
            ],
          },
        },
        {
          id: 'k6W8GGOSXnrS',
          ref: 'b08140d1-a53b-4162-80e5-0daca37148b8',
          title: 'And finally, which **spiritual benefits** are you interested in?',
          type: 'multiple_choice',
          validations: {
            required: true,
            min_selection: 1,
            max_selection: 3,
          },
          properties: {
            description: '_Select up to 3._',
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: '0f0eb005-cb72-4393-8ae7-895a12763718',
                id: 'tXdUbeutnOmb',
                label: 'Ancestral healing',
              },
              {
                ref: 'd5a725d1-c649-43e6-a2cb-954d8fdec436',
                id: '81wsH31ihiea',
                label: 'Attracting soulmate',
              },
              {
                ref: '6121de94-b05a-4383-9cc3-ec17f80c2a96',
                id: 'o0ontxPEj1eQ',
                label: 'Soul retrieval',
              },
              {
                ref: '5e5a70bc-6f48-4f1e-a981-520096dc10c2',
                id: 'QNlURRsrSnI0',
                label: 'Spiritual evolution',
              },
              {
                ref: '037f05f8-440e-45fa-9388-e387bb496db6',
                id: 'J30Bd07vrpeN',
                label: 'Curse undoing',
              },
              {
                ref: '566530e7-7af1-4dfe-9dd0-cd2981b725fb',
                id: 'OqWR9D95KByd',
                label: 'Managing Dark night of the Soul',
              },
              {
                ref: 'cf69ab6c-3dd7-4f93-a50e-ac2f79fc480a',
                id: 'QzDDkkcFTJuG',
                label: 'None of these',
              },
            ],
          },
        },
        {
          id: 'iVxvtbxgN1Bp',
          ref: '6474bef0-8393-40fe-9f5e-261ed4749737',
          title: 'Anything else you think we should know?',
          type: 'long_text',
          validations: {
            required: false,
          },
          properties: {
            description:
              'For example, how you feel or what you need right now 💜\n\nIf you’d like to connect with a specific practitioner, please share their name below.',
          },
        },
        {
          id: 'Hgx74049MfKa',
          ref: 'e6c565bc-5034-4897-8fc6-76b8bc030eb3',
          title: 'Now for the logistics...\n\nWhat time of day is best for a session?',
          type: 'multiple_choice',
          validations: {
            required: true,
          },
          properties: {
            randomize: false,
            allow_multiple_selection: true,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: '296d95d4-a5bb-40d0-a93c-bdff8d913464',
                id: 'JHXjcmGKn4Tn',
                label: 'Mornings',
              },
              {
                ref: 'b6618d27-aef9-49ba-b06d-6d4d6d401c2f',
                id: 'zWWeWdn9qgGK',
                label: 'Midday',
              },
              {
                ref: '1727b550-84ef-4fd3-acca-695453ed3eb3',
                id: '2nEeXAvOonjL',
                label: 'Afternoon',
              },
              {
                ref: 'e25f8dff-cd94-4921-a4bc-318510967b83',
                id: 'kAtNUAbZJUNh',
                label: 'Evenings',
              },
              {
                ref: '0607b23f-1fab-4067-af31-599fc9713583',
                id: '6qPSq9zThtPY',
                label: 'Weekends only',
              },
            ],
          },
        },
        {
          id: 'nLbZqqV5HpaE',
          ref: 'f94f0277-e4fc-4914-b741-326c79fb2ada',
          title: 'When choosing a healing session, how do you prioritize your decision?',
          type: 'multiple_choice',
          validations: {
            required: true,
          },
          properties: {
            randomize: false,
            allow_multiple_selection: false,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: '7035b0ce-c4e8-4a48-9eec-e1c7b86b0ec6',
                id: 'ZUgn7vj3f3EO',
                label: 'Price-first: "I am cost-conscious and prioritize session pricing."',
              },
              {
                ref: 'd5f05aed-756d-43ed-b74a-5dc5e3da37ff',
                id: 'jMN4JhOtnvAW',
                label: 'Profile-first: "Finding the right healer is my priority; price comes second."',
              },
              {
                ref: '415e2cd0-5ac8-4cd3-b68c-7f0f7eb165ea',
                id: '8QzOE71TScHs',
                label: 'Balanced: "I equally consider the healer’s profile and pricing."',
              },
              {
                ref: 'fff576ce-2a7b-4025-ba95-19ec5f17e5e0',
                id: 'kMkWqnD7IxSp',
                label: 'Intuition-first: "If a healer feels right, cost doesn\'t matter."',
              },
            ],
          },
        },
        {
          id: QUESTION_AGE_GROUP_ID,
          ref: '6bf50ccd-c028-4d1d-812d-17f603168d61',
          title: 'Please select your age group',
          type: 'multiple_choice',
          validations: {
            required: false,
          },
          properties: {
            randomize: false,
            allow_multiple_selection: false,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: 'c557c3c7-495e-46fc-8fac-c9debe9c3c3d',
                id: '2jDfsK0D35S0',
                label: 'Less than 18',
              },
              {
                ref: '6936dbf6-baad-4445-b720-09a87ee8f839',
                id: 'mDvPuAVSz3HI',
                label: '18-29',
              },
              {
                ref: '0eb719bd-853e-4ff4-8f9f-46270710dae1',
                id: 'MmxRK5LPys8h',
                label: '30-39',
              },
              {
                ref: 'c4891373-2151-4254-bb0a-a598843121b0',
                id: 'a369MTyr7a3h',
                label: '40-49',
              },
              {
                ref: 'c4c99c78-5154-4ff5-9159-a252cae26ea7',
                id: 'Ac1KMd74paHM',
                label: '50-59',
              },
              {
                ref: 'b170ab53-99a6-4493-af45-510c6001f0ec',
                id: 'kIf8ng4GMbhe',
                label: '60+',
              },
            ],
          },
        },
        {
          id: QUESTION_GENDER_IDENTIFY_ID,
          ref: 'e71c6614-7579-4e2f-8cff-98d315d5a4b1',
          title: 'How do you identify?',
          type: 'multiple_choice',
          validations: {
            required: false,
          },
          properties: {
            randomize: false,
            allow_multiple_selection: false,
            allow_other_choice: false,
            vertical_alignment: true,
            choices: [
              {
                ref: 'e7b7fdfd-b16c-4e51-a33d-5a3a61f74d13',
                id: 'nnDT2QQogxxM',
                label: 'Female',
              },
              {
                ref: '50438531-19b4-44e6-b20b-8fb021e2e804',
                id: '9S1gWQXpK6XO',
                label: 'Male',
              },
              {
                ref: '3e89a6d8-0912-4da8-bd29-fc7c0c3a5e47',
                id: 'rgWq0BDN2KY0',
                label: 'Non-binary',
              },
            ],
          },
        },
        {
          id: QUESTION_CONTACT_INFO_ID,
          ref: 'f1974a1e-7ace-446f-848f-349ca263c08f',
          title: 'And finally... ',
          type: 'contact_info',
          validations: {},
          properties: {
            contactFields: [
              {
                id: '9JuDAHTRkG0o',
                ref: '92e1dd8a-c876-416e-9c45-a1de2b20a1fd',
                title: 'First name',
                type: 'short_text',
                validations: {
                  required: true,
                },
                properties: {},
                subfield_key: 'first_name',
              },
              {
                id: 'KkY8IV7d0hDI',
                ref: 'a0d5fa9c-9794-4334-948b-29a6e760f93e',
                title: 'Last name',
                type: 'short_text',
                validations: {
                  required: true,
                },
                properties: {},
                subfield_key: 'last_name',
              },
              {
                id: 'KkY8AV7d0hDE',
                ref: 'a0d8fm1c-9794-4334-549b-29a6e760f93e',
                title: 'City, State/Country',
                type: 'short_text',
                validations: {
                  required: true,
                },
                properties: {},
                subfield_key: 'location',
              },
              {
                id: 'mCV72htR3MmM',
                ref: 'dcfe7871-6d7d-48c9-a138-a589e582e9f5',
                title: 'Email',
                type: 'email',
                validations: {
                  required: true,
                },
                properties: {},
                subfield_key: 'email',
              },
            ],
          },
        },
      ],
    },
    version: 1,
    answers: {} as Record<string, Answer>,
    submitStatus: null,
    recommendationRequestId: null,
    clientId: null,
  };
};

const clientRequestNewHealerSlice = createSlice({
  name: 'clientRequestNewHealer',
  initialState: initialClientRequestNewHealerState(),
  reducers: {
    setVersion: (state, action: PayloadAction<number>) => {
      state.version = action.payload;
    },
    storeAnswer: (state, action: PayloadAction<Answer>) => {
      const answer = action.payload;
      state.answers[answer.question.id] = answer;
    },
    setSubmitStatus: (state, action: PayloadAction<'loading' | 'fail' | 'success' | null>) => {
      state.submitStatus = action.payload;
    },
    setRecommendationRequestId: (state, action: PayloadAction<string>) => {
      state.recommendationRequestId = action.payload;
    },
    setClientId: (state, action: PayloadAction<string>) => {
      state.clientId = action.payload;
    },
    resetRequestForm: (state) => {
      state.answers = {};
      state.submitStatus = null;
      state.recommendationRequestId = null;
      state.clientId = null;
    },
  },
});

export const { resetRequestForm, storeAnswer, setSubmitStatus, setRecommendationRequestId, setClientId } = clientRequestNewHealerSlice.actions;
export default clientRequestNewHealerSlice.reducer;
